import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export type Theme = 'dark' | 'light';
const KEY = 'kj.theme';
const ThemeContext = createContext<{ theme: Theme; toggleTheme: () => void } | null>(null);

function savedTheme(): Theme {
  try { return localStorage.getItem(KEY) === 'light' ? 'light' : 'dark'; } catch { return 'dark'; }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(savedTheme);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    try { localStorage.setItem(KEY, theme); } catch { /* Private browsing may block storage; theme still works for this session. */ }
  }, [theme]);
  const toggleTheme = useCallback(() => setTheme(current => current === 'dark' ? 'light' : 'dark'), []);
  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('ThemeProvider missing');
  return value;
}

export function ThemeToggle({ compact = false }: { compact?: boolean }) {
  const { theme, toggleTheme } = useTheme();
  const next = theme === 'dark' ? 'light' : 'dark';
  return <button type="button" className="btn btn-ghost theme-toggle" onClick={toggleTheme}
    aria-label={`Switch to ${next} mode`} aria-pressed={theme === 'light'} title={`Switch to ${next} mode`}>
    <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span><span>{compact ? (theme === 'dark' ? 'Light' : 'Dark') : `${theme === 'dark' ? 'Light' : 'Dark'} mode`}</span>
  </button>;
}
